import React from "react";
import {
  CalendarBlank, Lightning, UsersThree, ScanSmiley, Stack, Bank, Vault, SlidersHorizontal, ArrowRight,
} from "@phosphor-icons/react";
import { useGov } from "@/hooks/governance";
import { govMeta, govActionLabel, govStatusTone, adaFromLovelace, asPct } from "@/lib/governance";
import { formatAdaBigCount } from "@/lib/format";
import { Badge } from "@/components/primitives";
import { Table } from "@/components/Table";
import {
  GovPageShell, GovSection, GovCard, GovRow, GovError, GovSkeleton, GovEmpty, useGovNav,
} from "@/components/governance/GovShell";
import { GovBarStat } from "@/components/governance/GovBarStat";
import { DrepPowerCard } from "@/components/governance/DrepPowerCard";
import { GovDigest } from "@/components/governance/GovDigest";
import { RecentActionsTable } from "@/components/governance/RecentActionsTable";
import { TreasurySparkline } from "@/components/governance/TreasurySparkline";
import { ThresholdsCard } from "@/components/governance/ThresholdsCard";

const Tally = ({ counts }) => {
  if (!counts) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-2 mono text-[12px] tabnum">
      <span className="text-emerald-400">{counts.yes ?? 0}</span>
      <span className="text-muted-foreground/40">/</span>
      <span className="text-red-400">{counts.no ?? 0}</span>
      <span className="text-muted-foreground/40">/</span>
      <span className="text-muted-foreground">{counts.abstain ?? 0}</span>
    </span>
  );
};

const ViewAll = ({ to, label = "View all" }) => {
  const navigate = useGovNav();
  return (
    <button
      type="button"
      data-testid={`view-all-${to}`}
      onClick={() => navigate(to)}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] mono text-primary hover:text-primary/80 transition-colors"
    >
      {label} <ArrowRight size={11} weight="bold" />
    </button>
  );
};

const proposalColumns = (navigate) => [
  {
    key: "type",
    label: "Type",
    render: (r) => (
      <button
        type="button"
        data-testid={`gov-proposal-${r.tx_hash}-${r.cert_index}`}
        onClick={() => navigate(`/governance/proposals/${r.tx_hash}/${r.cert_index}`)}
        className="text-left text-foreground hover:text-primary underline decoration-dotted decoration-border underline-offset-2 transition-colors"
        title={r.title || r.gov_action_id}
      >
        {govActionLabel(r.type)}
      </button>
    ),
  },
  { key: "status", label: "Status", render: (r) => <Badge tone={govStatusTone(r.status)}>{r.status}</Badge> },
  { key: "drep", label: "DRep (Y/N/A)", render: (r) => <Tally counts={r.vote_counts?.drep} /> },
  { key: "cc", label: "CC (Y/N/A)", render: (r) => <Tally counts={r.vote_counts?.constitutional_committee} /> },
  { key: "submitted", label: "Submitted", align: "right", render: (r) => <span className="text-muted-foreground">epoch {r.submitted_epoch ?? "—"}</span> },
  { key: "expires", label: "Expires", align: "right", render: (r) => <span className="text-muted-foreground">epoch {r.expires_after_epoch ?? "—"}</span> },
];

const recentColumns = (navigate) => [
  {
    key: "type",
    label: "Type",
    render: (r) => (
      <button
        type="button"
        onClick={() => navigate(`/governance/proposals/${r.tx_hash}/${r.cert_index}`)}
        className="text-left text-foreground hover:text-primary underline decoration-dotted decoration-border underline-offset-2 transition-colors"
        title={r.title || r.gov_action_id}
      >
        {govActionLabel(r.type)}
      </button>
    ),
  },
  { key: "status", label: "Status", render: (r) => <Badge tone={govStatusTone(r.status)}>{r.status}</Badge> },
  { key: "submitted", label: "Submitted", align: "right", render: (r) => <span className="text-muted-foreground">epoch {r.submitted_epoch ?? "—"}</span> },
  {
    key: "outcome",
    label: "Outcome",
    align: "right",
    render: (r) =>
      r.enacted_epoch ? <span className="text-emerald-400">enacted {r.enacted_epoch}</span>
        : r.ratified_epoch ? <span className="text-emerald-400">ratified {r.ratified_epoch}</span>
          : r.expired_epoch ? <span className="text-muted-foreground">expired {r.expired_epoch}</span>
            : r.dropped_epoch ? <span className="text-red-400">dropped {r.dropped_epoch}</span>
              : <span className="text-muted-foreground">—</span>,
  },
];

export const GovernanceOverview = () => {
  const navigate = useGovNav();
  const meta = govMeta("overview");

  const vp = useGov("/governance/voting_power");
  const committee = useGov("/governance/committee");
  const treasury = useGov("/governance/treasury");
  const live = useGov("/governance/proposals/live");
  const recent = useGov("/governance/proposals", { params: { count: 6, page: 1 } });
  const params = useGov("/governance/parameters");
  const constitution = useGov("/governance/constitution");

  const d = vp.data;
  const drepTotalDreps = d ? (d.drep?.active_dreps ?? 0) + (d.drep?.inactive_dreps ?? 0) : null;
  const lastTreasury = Array.isArray(treasury.data?.epochs) && treasury.data.epochs.length
    ? treasury.data.epochs[treasury.data.epochs.length - 1]
    : null;
  const liveList = Array.isArray(live.data) ? live.data : [];
  const recentList = Array.isArray(recent.data) ? recent.data : [];
  const spoVal = d?.spo_unavailable_reason
    ? "Unavailable"
    : d?.spo ? `${formatAdaBigCount(d.spo.voting_power)} ₳` : "—";

  // ---- gauge metrics (honest, data-derived) ----
  const pct = (num, den) => (den ? Math.max(0, Math.min(1, num / den)) : null);
  const drepPow = d?.drep?.total != null ? Number(d.drep.total) : 0;
  const spoPow = d?.spo && !d.spo_unavailable_reason ? Number(d.spo.voting_power) : 0;
  const totPow = drepPow + spoPow;
  const drepShare = totPow ? drepPow / totPow : null;
  const spoShare = totPow ? spoPow / totPow : null;
  const drepActivePct = pct(d?.drep?.active_dreps, drepTotalDreps);
  const commPct = pct(d?.committee?.members, d?.committee?.eligible_members);
  const treasuryPct = lastTreasury && lastTreasury.reserves != null
    ? pct(Number(lastTreasury.treasury), Number(lastTreasury.treasury) + Number(lastTreasury.reserves))
    : null;
  const major = params.data?.protocol_version?.major;
  const asRing = (p) => (p == null ? { fill: 0.06, text: "—", dim: true } : { fill: p, text: `${Math.round(p * 100)}%` });
  const loadingRing = { fill: 0.06, text: "…", dim: true };

  // real treasury change vs previous epoch (no fabricated deltas)
  const tEpochs = treasury.data?.epochs || [];
  const treasuryDelta = (() => {
    const valid = tEpochs.filter((e) => e && e.treasury != null);
    if (valid.length < 2) return null;
    const last = Number(valid[valid.length - 1].treasury);
    const prev = Number(valid[valid.length - 2].treasury);
    if (!prev) return null;
    const pct = ((last - prev) / prev) * 100;
    return { dir: pct > 0 ? "up" : pct < 0 ? "down" : "neutral", text: `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%` };
  })();

  return (
    <GovPageShell
      crumbs={meta.crumbs}
      title={meta.title}
      subtitle={d ? `Cardano on-chain governance · epoch ${d.epoch} · data ${d.data_status}` : "Cardano on-chain governance"}
    >
      {/* ---------- top stats (horizontal progress bars) ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="gov-overview-stats">
        <GovBarStat testid="stat-epoch" label="Current Epoch" icon={CalendarBlank}
          value={d ? d.epoch : (vp.loading ? "…" : "—")} hint={d ? `${d.data_status}` : null}
          fill={0.5} />
        <GovBarStat testid="stat-live" label="Live Actions" icon={Lightning}
          value={live.loading && !live.data ? "…" : liveList.length}
          hint="open for voting" onClick={() => navigate("/governance/proposals/live")}
          fill={liveList.length ? Math.min(0.15 + liveList.length * 0.08, 0.95) : 0.04} />
        <GovBarStat testid="stat-dreps" label="Registered DReps" icon={UsersThree}
          value={drepTotalDreps ?? (vp.loading ? "…" : "—")}
          hint={d ? `${d.drep?.active_dreps ?? 0} active · ${d.drep?.inactive_dreps ?? 0} inactive` : null}
          onClick={() => navigate("/governance/dreps")}
          fill={(vp.loading && !d ? loadingRing : asRing(drepActivePct)).fill} />
        <GovBarStat testid="stat-drep-power" label="DRep Voting Power" icon={ScanSmiley}
          value={d ? `${formatAdaBigCount(d.drep?.total)} ₳` : (vp.loading ? "…" : "—")}
          hint="share of total"
          fill={(vp.loading && !d ? loadingRing : asRing(drepShare)).fill} />
        <GovBarStat testid="stat-spo-power" label="SPO Voting Power" icon={Stack}
          value={vp.loading && !d ? "…" : spoVal}
          hint={d?.spo ? `${d.spo.pools?.toLocaleString?.() ?? d.spo.pools} pools` : null}
          onClick={() => navigate("/governance/pools")}
          fill={(vp.loading && !d ? loadingRing : asRing(spoShare)).fill} />
        <GovBarStat testid="stat-committee" label="Committee" icon={Bank}
          value={d?.committee ? `${d.committee.members}/${d.committee.eligible_members}` : (vp.loading ? "…" : "—")}
          hint={d?.committee ? `quorum ${d.committee.quorum?.numerator}/${d.committee.quorum?.denominator}` : null}
          onClick={() => navigate("/governance/committee")}
          fill={(vp.loading && !d ? loadingRing : asRing(commPct)).fill} />
        <GovBarStat testid="stat-treasury" label="Treasury" icon={Vault}
          value={lastTreasury ? `${formatAdaBigCount(lastTreasury.treasury)} ₳` : (treasury.loading ? "…" : "—")}
          hint={lastTreasury ? `epoch ${lastTreasury.epoch}` : null}
          delta={treasuryDelta}
          onClick={() => navigate("/governance/treasury")}
          fill={(treasury.loading && !lastTreasury ? loadingRing : asRing(treasuryPct)).fill} />
        <GovBarStat testid="stat-protocol" label="Protocol Version" icon={SlidersHorizontal}
          value={params.data?.protocol_version ? `v${params.data.protocol_version.major}.${params.data.protocol_version.minor}` : (params.loading ? "…" : "—")}
          hint={params.data ? (params.data.bootstrap_phase ? "bootstrap phase" : "full governance") : null}
          delta={params.data ? { dir: "neutral", text: "current" } : null}
          onClick={() => navigate("/governance/parameters")}
          fill={major != null ? Math.min((major || 0) / 15, 0.95) : 0.06} />
      </div>

      {/* ---------- DRep voting power (full width) ---------- */}
      <GovSection testid="gov-drep-card" title="DRep Voting Power" action={<ViewAll to="/governance/dreps" label="DReps" />}>
        {vp.error ? <GovError error={vp.error} onRetry={vp.refetch} /> : vp.loading && !d ? <GovSkeleton lines={4} /> : d ? (
          <GovCard>
            <DrepPowerCard d={d} />
          </GovCard>
        ) : <GovEmpty />}
      </GovSection>

      {/* ---------- committee + constitution (even halves) ---------- */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="gov-overview-cards">
        {/* Committee summary */}
        <GovSection testid="gov-committee-card" title="Constitutional Committee" action={<ViewAll to="/governance/committee" label="Details" />}>
          {committee.error ? <GovError error={committee.error} onRetry={committee.refetch} /> : committee.loading && !committee.data ? <GovSkeleton lines={4} /> : committee.data ? (
            <GovCard>
              <GovRow label="Members">{committee.data.members?.length ?? d?.committee?.members ?? "—"}</GovRow>
              <GovRow label="Quorum">{committee.data.quorum ? `${committee.data.quorum.numerator}/${committee.data.quorum.denominator}` : "—"}</GovRow>
              <GovRow label="No Confidence">{committee.data.no_confidence ? <Badge tone="danger">yes</Badge> : <Badge tone="success">no</Badge>}</GovRow>
              <GovRow label="Enacted By">{committee.data.enacted_by?.enacted_epoch ? `epoch ${committee.data.enacted_by.enacted_epoch}` : "—"}</GovRow>
            </GovCard>
          ) : <GovEmpty />}
        </GovSection>

        {/* Constitution */}
        <GovSection testid="gov-constitution-card" title="Constitution" action={<ViewAll to="/governance/constitution" label="View" />}>
          {constitution.error ? <GovError error={constitution.error} onRetry={constitution.refetch} /> : constitution.loading && !constitution.data ? <GovSkeleton lines={4} /> : constitution.data ? (
            <GovCard>
              <GovRow label="Effective From">{constitution.data.effective_from_epoch ? `epoch ${constitution.data.effective_from_epoch}` : "—"}</GovRow>
              <GovRow label="Anchor Hash">{constitution.data.anchor?.data_hash ? `${constitution.data.anchor.data_hash.slice(0, 12)}…` : "—"}</GovRow>
              <GovRow label="Guardrail Script">{constitution.data.guardrail_script_hash ? `${constitution.data.guardrail_script_hash.slice(0, 12)}…` : "—"}</GovRow>
              <GovRow label="Current">{constitution.data.current ? <Badge tone="success">active</Badge> : <Badge>inactive</Badge>}</GovRow>
            </GovCard>
          ) : <GovEmpty />}
        </GovSection>
      </div>

      {/* ---------- treasury trend + key params ---------- */}
      <div className="grid md:grid-cols-2 gap-4 items-stretch">
        <div className="min-w-0 flex flex-col">
        <GovSection testid="gov-treasury-card" title="Treasury Trend" className="flex-1 flex flex-col" action={<ViewAll to="/governance/treasury" label="Treasury" />}>
          {treasury.error ? <GovError error={treasury.error} onRetry={treasury.refetch} /> : treasury.loading && !treasury.data ? <GovSkeleton lines={4} /> : lastTreasury ? (
            <GovCard className="flex-1">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex flex-wrap gap-x-8 gap-y-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">Current balance</div>
                    <div className="text-[15px] md:text-[17px] mono tabnum text-[#22d3ee] text-glow-cyan mt-1 whitespace-nowrap">{adaFromLovelace(lastTreasury.treasury)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">Reserves</div>
                    <div className="text-[15px] md:text-[17px] mono tabnum text-foreground mt-1 whitespace-nowrap">{adaFromLovelace(lastTreasury.reserves)}</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-black/20 px-2.5 py-1 text-[10px] mono text-muted-foreground shrink-0 whitespace-nowrap">
                  <CalendarBlank size={12} className="text-primary/70" />
                  Epochs {treasury.data.from_epoch}–{treasury.data.to_epoch}
                </span>
              </div>
              <TreasurySparkline epochs={treasury.data.epochs} height={200} detailed />
              <div className="text-[10px] mono text-muted-foreground/70 mt-1">source {treasury.data.source}</div>
            </GovCard>
          ) : <GovEmpty />}
        </GovSection>
        </div>

        <div className="min-w-0 flex flex-col">
        <GovSection testid="gov-params-card" title="Key Voting Thresholds" className="flex-1 flex flex-col" action={<ViewAll to="/governance/parameters" label="Parameters" />}>
          {params.error ? <GovError error={params.error} onRetry={params.refetch} /> : params.loading && !params.data ? <GovSkeleton lines={4} /> : params.data ? (
            <GovCard className="flex-1 flex flex-col">
              <ThresholdsCard params={params.data} />
            </GovCard>
          ) : <GovEmpty />}
        </GovSection>
        </div>
      </div>

      {/* ---------- live actions (expandable digest table) ---------- */}
      <GovDigest onGovSearch={navigate} title="Live Governance Actions" showWatchlist={false} testid="gov-live-section" />

      {/* ---------- recent actions ---------- */}
      <GovSection testid="gov-recent-section" title="Recent Governance Actions" action={<ViewAll to="/governance/proposals" />}>
        {recent.error ? (
          <GovError error={recent.error} onRetry={recent.refetch} />
        ) : recent.loading && !recent.data ? (
          <GovSkeleton lines={6} />
        ) : (
          <GovCard>
            <RecentActionsTable rows={recentList} currentEpoch={d?.epoch} onView={(r) => navigate(`/governance/proposals/${r.tx_hash}/${r.cert_index}`)} />
          </GovCard>
        )}
      </GovSection>
    </GovPageShell>
  );
};
